import Link, { type LinkProps } from "@/infrastructure/router/Link";
import type { ReactNode } from "react";

export interface ListRowProps {
	readonly title: ReactNode;
	readonly subtitle?: ReactNode;
	readonly leading?: ReactNode;
	readonly trailing?: ReactNode;
	readonly href?: LinkProps<string>["href"];
	readonly onClick?: () => void;
	readonly disabled?: boolean;
	readonly className?: string;
}

/**
 * ListRow — generic list row with leading/title/subtitle/trailing slots.
 * Renders as a link, button, or static div depending on props. Mirrors
 * `DSListRow` on iOS.
 */
export function ListRow({
	title,
	subtitle,
	leading,
	trailing,
	href,
	onClick,
	disabled = false,
	className = "",
}: Readonly<ListRowProps>) {
	const content = (
		<>
			{leading === undefined ? null : (
				<span className="ds-list-row__leading" aria-hidden="true">
					{leading}
				</span>
			)}
			<span className="ds-list-row__copy">
				<strong>{title}</strong>
				{subtitle === undefined ? null : <small>{subtitle}</small>}
			</span>
			{trailing === undefined ? null : (
				<span className="ds-list-row__trailing">{trailing}</span>
			)}
		</>
	);
	if (href !== undefined) {
		return (
			<Link className={`ds-list-row ${className}`.trim()} href={href}>
				{content}
			</Link>
		);
	}
	if (onClick !== undefined) {
		return (
			<button
				className={`ds-list-row ${className}`.trim()}
				type="button"
				onClick={onClick}
				disabled={disabled}
			>
				{content}
			</button>
		);
	}
	return (
		<div className={`ds-list-row ${className}`.trim()} aria-disabled={disabled}>
			{content}
		</div>
	);
}
